import { Schema } from "@upserve/cold-storage";
import { compareAsc } from "date-fns";
import { find, sort, union, without } from "ramda";
import { Maybe } from "true-myth";

const { jsontype, propertygroup } = Schema;

import { maybetype, ShiftTimeSchema } from "helpers/schema";
import {
  roleSchema,
  posUserSchema,
  unavailabilityBlockSchema,
} from "helpers/transforms/pos_users";
import { IPerson, IPeople, IPosUser, IShift, ISchedule } from "types/schedules";

const shiftSchema: IShift = {
  role: roleSchema,
  startTime: propertygroup(ShiftTimeSchema("start")),
  endTime: propertygroup(ShiftTimeSchema("end")),
  uuid: jsontype("string"),
};

const personSchema: IPerson = {
  assignee: maybetype(posUserSchema),
  shifts: [shiftSchema],
  unavail: maybetype([unavailabilityBlockSchema]),
};

function compareShiftTimes(shiftA: IShift, shiftB: IShift): number {
  return compareAsc(shiftA.startTime, shiftB.startTime);
}

function findByUuid(id: string, people: IPeople): Maybe<IPerson> {
  return Maybe.of(
    find((shiftGroup) => {
      return shiftGroup.assignee.match({
        Just: (person: IPosUser) => person.uuid === id,
        Nothing: () => false,
      });
    }, people)
  );
}

function addPersonToSchedule(
  newCompleteSchedule: ISchedule,
  newPerson: IPerson
): ISchedule {
  return {
    ...newCompleteSchedule,
    people: [...newCompleteSchedule.people, sortAssigneesShifts(newPerson)],
  };
}

function mergeUserShifts(existingPerson: IPerson, newPerson: IPerson): IPerson {
  return {
    ...existingPerson,
    shifts: [...existingPerson.shifts, ...newPerson.shifts],
  };
}

function removePersonFromSchedule(
  schedule: ISchedule,
  personToRemove: IPerson
): ISchedule {
  return {
    ...schedule,
    people: without([personToRemove], schedule.people),
  };
}

function sortAssigneesShifts(assignee: IPerson): IPerson {
  return {
    ...assignee,
    shifts: sort(compareShiftTimes, assignee.shifts),
  };
}

// Merges the shifts of 2 instances of the same person and replaces them
function updatePersonInSchedule(
  newCompleteSchedule: ISchedule,
  newPerson: IPerson,
  existingPerson: IPerson
): ISchedule {
  const updatedPerson = mergeUserShifts(existingPerson, newPerson);

  return replacePersonInSchedule(
    newCompleteSchedule,
    updatedPerson,
    existingPerson
  );
}

function replacePersonInSchedule(
  newCompleteSchedule: ISchedule,
  updatedPerson: IPerson,
  existingPerson: IPerson
): ISchedule {
  return {
    ...newCompleteSchedule,
    people: union(
      [sortAssigneesShifts(updatedPerson)],
      removePersonFromSchedule(newCompleteSchedule, existingPerson).people
    ),
  };
}

export {
  addPersonToSchedule,
  compareShiftTimes,
  findByUuid,
  mergeUserShifts,
  personSchema,
  removePersonFromSchedule,
  replacePersonInSchedule,
  sortAssigneesShifts,
  updatePersonInSchedule,
};
